import React from "react";
import "./Loader.css";

/**
 * Reusable loader component with dot animation
 * Can be used for:
 * - API calls loading states
 * - Chart loading states
 * - Skeleton fallbacks
 * - Full page loading
 *
 * @param {string} size - "sm" | "md" | "lg" (default: "md")
 * @param {string} variant - "dots" | "spinner" | "pulse" (default: "dots")
 * @param {string} label - Optional loading text
 * @param {boolean} fullScreen - Fill entire screen (default: false)
 */
export const Loader = ({
  size = "md",
  variant = "dots",
  label = "Loading",
  fullScreen = false,
}) => {
  const containerClass = `loader-container loader-${size} ${
    fullScreen ? "loader-fullscreen" : ""
  }`;

  return (
    <div className={containerClass}>
      <div className={`loader loader-${variant}`}>
        <div className="dot dot-1"></div>
        <div className="dot dot-2"></div>
        <div className="dot dot-3"></div>
      </div>
      {label && <p className="loader-label">{label}</p>}
    </div>
  );
};

/**
 * Skeleton loader for tables and lists
 */
export const SkeletonLoader = ({ rows = 5, columns = 5 }) => {
  return (
    <div className="space-y-2">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex gap-2">
          {Array.from({ length: columns }).map((_, j) => (
            <div
              key={j}
              className="h-10 bg-gray-200 rounded animate-pulse flex-1"
            ></div>
          ))}
        </div>
      ))}
    </div>
  );
};

/**
 * Skeleton loader for charts
 */
export const ChartSkeletonLoader = () => {
  return (
    <div className="space-y-4">
      <div className="h-8 bg-gray-200 rounded animate-pulse w-1/4"></div>
      <div className="h-64 bg-gray-200 rounded animate-pulse"></div>
    </div>
  );
};

/**
 * Inline loader (small dots)
 */
export const InlineLoader = ({ label = "Processing" }) => {
  return (
    <div className="flex items-center gap-2">
      <div className="loader loader-dots-sm">
        <div className="dot dot-1"></div>
        <div className="dot dot-2"></div>
        <div className="dot dot-3"></div>
      </div>
      <span className="text-sm text-gray-600">{label}</span>
    </div>
  );
};

export default Loader;
